import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import et from "../../messages/et.json";
import en from "../../messages/en.json";
import { translate, type Vars } from "./message";

type Messages = Record<string, unknown>;
const MESSAGES: Record<string, Messages> = { et, en };
const STORAGE_KEY = "wa_crm_locale";

interface I18nValue {
  locale: string;
  setLocale: (l: string) => void;
  t: (key: string, vars?: Vars) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<string>(() => {
    if (typeof window === "undefined") return "et";
    return window.localStorage.getItem(STORAGE_KEY) ?? "et";
  });

  const setLocale = useCallback((l: string) => {
    setLocaleState(l);
    if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, l);
  }, []);

  const t = useCallback(
    (key: string, vars?: Vars) => translate([MESSAGES[locale], MESSAGES.et], key, vars),
    [locale],
  );

  // The browser tab title follows the interface language. index.html supplies
  // the first value before React loads; this updates it on a language change.
  useEffect(() => {
    if (typeof document !== "undefined") document.title = t("app.browserTitle");
  }, [t]);

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}

export function useT() {
  return useI18n().t;
}
